import React from "react";
import {
    Autocomplete, Box, Button, Chip, CircularProgress, TextField, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useAddVendorProfileQuotationForPdfMailMutation, useLazySearchVendorQuotationsQuery } from "store/services/vendorQuotationApi";
import { useLazySearchVendorsQuery } from "store/services/vendorProfileApi";
import { useAuth } from "hooks";

const breadcrumbs = [
    { title: "Quotation VendorProfile List", path: "/app/crm/vendor-lead/quotation-list-vendor" },
    { title: "Add PDF Quotation" },
];

const AddVendorQuotationPdfMailScreen = () => {
    const navigate = useNavigate();
    const { viewEntity } = useAuth();
    const entity = viewEntity?.entity?._id;
    const account = viewEntity?.entity?.account?._id;

    const [vendorProfileInput, setVendorProfileInput] = React.useState("");
    const [selectedVendorProfiles, setSelectedVendorProfiles] = React.useState([]);

    const [quotationInput, setQuotationInput] = React.useState("");
    const [selectedQuotations, setSelectedQuotations] = React.useState([]);

    const [vendorProfileTouched, setVendorProfileTouched] = React.useState(false);
    const [quotationTouched, setQuotationTouched] = React.useState(false);

    const [triggerSearchVendorProfiles, { data: vendorProfileData, isFetching: fetchingVendorProfiles }] =
        useLazySearchVendorsQuery();
    const vendorProfileOptions = vendorProfileData?.data ?? [];

    const [triggerSearchQuotations, { data: quotationOptions = [], isFetching: fetchingQuotations }] =
        useLazySearchVendorQuotationsQuery();

    const [addPdfMail, { isLoading: submitting }] =
        useAddVendorProfileQuotationForPdfMailMutation();

    React.useEffect(() => {
        if (vendorProfileInput.length < 3) return;
        const t = setTimeout(() => triggerSearchVendorProfiles({ account, entity, query: vendorProfileInput }), 300);
        return () => clearTimeout(t);
    }, [account, entity, vendorProfileInput, triggerSearchVendorProfiles]);

    React.useEffect(() => {
        if (quotationInput.length < 3) return;
        const t = setTimeout(() => triggerSearchQuotations({ q: quotationInput }), 300);
        return () => clearTimeout(t);
    }, [quotationInput, triggerSearchQuotations]);

    const handleSubmit = async () => {
        setVendorProfileTouched(true);
        setQuotationTouched(true);
        if (!selectedVendorProfiles.length || !selectedQuotations.length) return;

        try {
            await Promise.all(
                selectedVendorProfiles.flatMap((vendorProfile) =>
                    selectedQuotations.map((quotation) =>
                        addPdfMail({
                            vendorId: vendorProfile._id,
                            vendorQuotationId: quotation._id,
                            account,
                            entity
                        }).unwrap()
                    )
                )
            );
            toast.success("PDF quotation email(s) sent successfully");
            navigate("/app/crm/vendor-lead/quotation-list-vendor");
        } catch (err) {
            toast.error(err?.data?.message ?? err?.message ?? "Could not send email");
        }
    };

    const chipSx = {
        bgcolor: "#2a2a2a",
        color: "#e0e0e0",
        border: "1px solid #444",
        fontWeight: 400,
        "& .MuiChip-deleteIcon": { color: "#888", "&:hover": { color: "#bbb" } },
    };

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Paper>
                <Box sx={{ maxWidth: 560, mx: "auto", px: 3, py: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                    <Typography variant="h6" fontWeight={600}>
                        Send PDF Quotation to VendorProfile
                    </Typography>

                    {/* VendorProfile multi-select */}
                    <Autocomplete
                        multiple
                        options={vendorProfileOptions}
                        getOptionLabel={(o) => `${o.shopName ?? ""} - ${o.ownerName ?? ""}`}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        loading={fetchingVendorProfiles}
                        filterOptions={(x) => x}
                        inputValue={vendorProfileInput}
                        value={selectedVendorProfiles}
                        onInputChange={(_, val, reason) => {
                            if (reason !== "reset") setVendorProfileInput(val);
                        }}
                        onChange={(_, val) => {
                            setSelectedVendorProfiles(val);
                            setVendorProfileTouched(true);
                            setVendorProfileInput("");
                        }}
                        noOptionsText={vendorProfileInput.length < 3 ? "Type at least 3 characters to search" : "No vendorProfiles found"}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option._id}
                                    label={`${option.shopName ?? ""} - ${option.ownerName ?? ""}`}
                                    size="small"
                                    sx={chipSx}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select VendorProfile(s)"
                                placeholder={selectedVendorProfiles.length ? "Add more…" : "Search by name or ID…"}
                                required
                                error={vendorProfileTouched && !selectedVendorProfiles.length}
                                helperText={vendorProfileTouched && !selectedVendorProfiles.length ? "At least one Vendor Profile is required" : " "}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {fetchingVendorProfiles && <CircularProgress color="inherit" size={16} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Quotation multi-select */}
                    <Autocomplete
                        multiple
                        options={quotationOptions}
                        getOptionLabel={(o) => `${o.title ?? ""} - ${o.vendorQuotationId ?? ""}`}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        loading={fetchingQuotations}
                        filterOptions={(x) => x}
                        inputValue={quotationInput}
                        value={selectedQuotations}
                        onInputChange={(_, val, reason) => {
                            if (reason !== "reset") setQuotationInput(val);
                        }}
                        onChange={(_, val) => {
                            setSelectedQuotations(val);
                            setQuotationTouched(true);
                            setQuotationInput("");
                        }}
                        noOptionsText={quotationInput.length < 3 ? "Type at least 3 characters to search" : "No quotations found"}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option._id}
                                    label={`${option.title ?? ""} - ${option.vendorQuotationId ?? ""}`}
                                    size="small"
                                    sx={chipSx}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select PDF Quotation(s)"
                                placeholder={selectedQuotations.length ? "Add more…" : "Search by title or ID…"}
                                required
                                error={quotationTouched && !selectedQuotations.length}
                                helperText={quotationTouched && !selectedQuotations.length ? "At least one quotation is required" : " "}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {fetchingQuotations && <CircularProgress color="inherit" size={16} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Actions */}
                    <Box display="flex" gap={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/app/crm/vendor-lead/quotation-list-vendor")}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            variant="contained"
                            color="error"
                            loading={submitting}
                            onClick={handleSubmit}
                        >
                            Send PDF Email
                        </LoadingButton>
                    </Box>
                </Box>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default AddVendorQuotationPdfMailScreen;